/**
 * Approvals API service — wraps /api/approvals/*
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
export type SubjectKind =
  | 'communication_template'
  | 'media_asset'
  | 'workflow_template'
  | 'doc_type'
  | 'campaign'
  | 'content_asset';

export type ApprovalDecision =
  | 'approved'
  | 'rejected'
  | 'requested_changes'
  | 'withdrawn';

export interface ApprovalWorkflow {
  id: number;
  name: string;
  description?: string;
  scope: SubjectKind;
  scope_filter?: Record<string, any>;
  steps: Array<{ order: number; name?: string; role?: string; approvers_n?: number }>;
  is_default: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ApprovalRequest {
  id: number;
  subject_kind: SubjectKind;
  subject_id: number;
  subject_label?: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'superseded';
  current_step: number;
  submitted_by: number;
  submitted_at: string;
  closed_at?: string | null;
  workflow_name?: string;
  steps?: any[];
}

class ApprovalsAPI {
  private client: AxiosInstance;
  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async listWorkflows(scope?: SubjectKind) {
    const r = await this.client.get('/api/approvals/workflows', { params: scope ? { scope } : {} });
    return r.data as ApprovalWorkflow[];
  }

  async createWorkflow(workflow: Omit<ApprovalWorkflow, 'id' | 'created_at' | 'updated_at'>) {
    const r = await this.client.post('/api/approvals/workflows', workflow);
    return r.data as { id: number };
  }

  async deleteWorkflow(id: number) {
    return (await this.client.delete(`/api/approvals/workflows/${id}`)).data;
  }

  async submit(payload: {
    subject_kind: SubjectKind;
    subject_id: number;
    subject_version?: number;
    subject_label?: string;
    summary?: string;
  }) {
    return (await this.client.post('/api/approvals/requests', payload)).data;
  }

  async listRequests(params: {
    status?: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'superseded';
    subject_kind?: SubjectKind;
    subject_id?: number;
    limit?: number;
    offset?: number;
  } = {}) {
    const r = await this.client.get('/api/approvals/requests', { params });
    return r.data as ApprovalRequest[];
  }

  async getRequest(requestId: number) {
    const r = await this.client.get(`/api/approvals/requests/${requestId}`);
    return r.data;
  }

  async decide(requestId: number, decision: ApprovalDecision, notes?: string) {
    const r = await this.client.post(`/api/approvals/requests/${requestId}/decide`, {
      decision,
      notes,
    });
    return r.data;
  }
}

export const approvalsAPI = new ApprovalsAPI();
